// Horizontal swipe between the bottom tabs.
//
// Returns props to spread on the tab CONTENT container. Swipe left = next tab,
// right = previous; the ends don't wrap.
//
// TWO THINGS THAT BIT US, both worth keeping in mind before "simplifying" this:
//
// 1. TOUCH EVENTS, NOT POINTER EVENTS.
//    The first version listened to pointerdown/pointerup. In the Android
//    WebView the browser fires `pointercancel` as soon as it starts treating a
//    drag as a scroll, so `pointerup` frequently never arrived and the swipe was
//    simply never seen. touchstart/touchend always land. Mouse is handled
//    separately for the desktop-narrow case.
//
// 2. DON'T SNIFF CSS TO FIND HORIZONTAL SCROLLERS.
//    It used to walk up the DOM looking for `overflow-x: auto|scroll`. That
//    matches almost everything: per CSS, if one axis is not `visible` the other
//    computes to `auto`, so every `overflowY: 'auto'` list ALSO reports
//    `overflow-x: auto` — and any incidental pixel of horizontal overflow made
//    the check fire. Result: every swipe was discarded.
//    Genuine horizontal scrollers are now tagged `data-hscroll` explicitly
//    (CommonDevicesStrip, AutoHorizontalScroll) and only those are skipped.

import { useCallback, useRef } from 'react';

const DISTANCE = 55;   // px of horizontal travel to count as a swipe
const RATIO = 1.4;     // how much more horizontal than vertical it must be
const MAX_MS = 800;    // a slower drag is a scroll, not a swipe

// Only skip elements explicitly marked as horizontal scrollers.
function startsInsideHorizontalScroller(target) {
  return !!(target && target.closest && target.closest('[data-hscroll]'));
}

export default function useSwipeNav(index, count, onChange) {
  const start = useRef(null);

  const begin = useCallback((x, y, target) => {
    if (startsInsideHorizontalScroller(target)) { start.current = null; return; }
    start.current = { x, y, t: Date.now() };
  }, []);

  const finish = useCallback((x, y) => {
    const s = start.current;
    start.current = null;
    if (!s) return;

    const dx = x - s.x;
    const dy = y - s.y;
    if (Date.now() - s.t > MAX_MS) return;
    if (Math.abs(dx) < DISTANCE) return;
    if (Math.abs(dx) < Math.abs(dy) * RATIO) return;

    const next = dx < 0 ? index + 1 : index - 1;
    if (next >= 0 && next < count) onChange(next);
  }, [index, count, onChange]);

  return {
    onTouchStart: (e) => {
      const t = e.touches[0];
      if (t) begin(t.clientX, t.clientY, e.target);
    },
    onTouchEnd: (e) => {
      const t = e.changedTouches[0];
      if (t) finish(t.clientX, t.clientY);
    },
    onTouchCancel: () => { start.current = null; },

    // Mouse only — touch is handled above, so this never double-fires.
    onPointerDown: (e) => {
      if (e.pointerType !== 'mouse') return;
      begin(e.clientX, e.clientY, e.target);
    },
    onPointerUp: (e) => {
      if (e.pointerType !== 'mouse') return;
      finish(e.clientX, e.clientY);
    },
  };
}
